import { useCallback, useMemo, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { PasswordInput } from '@user/EditPasswordForm';
import { trpcClient } from '@framework/trpcClient';
import { classNames } from '@framework/utils';
import { TRPCClientError } from '@trpc/client';
import * as EmailValidator from 'email-validator';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
};

/**
 * ログイン中ユーザー自身のメールアドレス変更を申請するモーダル。
 *
 * 現在のパスワードとともに新しいメールアドレスをバックエンドへ送り、パスワード照合・確認メール
 * 送信までを1回のtRPC呼び出しで行う(Firebase Authのverify-and-change-email機能は本番環境で
 * Email/Passwordプロバイダが無効化されているため利用できない。パスワードリセットと同じ、
 * 自前のトークン発行+SendGridでの送信方式で確定は MailAddressChangePage が行う)。
 */
export const EmailChangeModal: React.FC<Props> = ({ isOpen, onClose, userEntity }: Props) => {
  const { t } = useTranslation();
  const [newMailAddress, setNewMailAddress] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleChangeNewMailAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNewMailAddress(event.target.value);
  }, []);

  const handleChangeCurrentPassword = useCallback((password: string) => setCurrentPassword(password), []);
  const validateOnBlur = useCallback(() => {
    setCurrentPasswordError(currentPassword === '' ? t('auth.passwordChange.modal.currentPasswordRequired') : '');
  }, [currentPassword, t]);

  const isValid = useMemo(
    () => EmailValidator.validate(newMailAddress) && currentPassword !== '',
    [newMailAddress, currentPassword]
  );

  const handleClose = useCallback(() => {
    setProcessing(false);
    setNewMailAddress('');
    setCurrentPassword('');
    setCurrentPasswordError('');
    setError('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;

    setProcessing(true);
    setError('');
    setCurrentPasswordError('');

    try {
      await trpcClient.user.auth.requestMailAddressChange.mutate({ currentPassword, newMailAddress });
      toast.success(t('auth.mailAddressChange.modal.sentMessage'));
      handleClose();
    } catch (error) {
      const code = error instanceof TRPCClientError ? error.shape?.data?.code : undefined;
      if (code === 'PRECONDITION_FAILED') {
        setCurrentPasswordError(t('auth.passwordChange.modal.currentPasswordIncorrect'));
      } else if (code === 'CONFLICT') {
        setError(t('auth.mailAddressChange.modal.alreadyInUse'));
      } else {
        setError(t('auth.mailAddressChange.modal.sendError'));
      }
      console.error('Failed to request mail address change:', error);
      setProcessing(false);
    }
  }, [currentPassword, handleClose, isValid, newMailAddress, t]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.mailAddressChange.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div className="pb-2 font-bold">{t('auth.editUser.form.emailLabel')}</div>
        <input
          type="text"
          className="cursor-not-allowed rounded-sm border bg-gray-200 p-2"
          defaultValue={userEntity.mailAddress}
          disabled
        />

        <div className="pb-2 pt-8 font-bold">{t('auth.mailAddressChange.modal.newMailAddressLabel')}</div>
        <input
          type="text"
          className={classNames('rounded-sm border p-2', processing ? 'cursor-not-allowed bg-gray-200' : '')}
          value={newMailAddress}
          onChange={handleChangeNewMailAddress}
          autoFocus
          disabled={processing}
        />

        <div className="pb-2 pt-8 font-bold">{t('auth.passwordChange.modal.currentPasswordLabel')}</div>
        <PasswordInput
          value={currentPassword}
          onChange={handleChangeCurrentPassword}
          onBlur={validateOnBlur}
          error={currentPasswordError}
          disabled={processing}
        />

        <div className="pt-8 text-sm">{t('auth.mailAddressChange.modal.description')}</div>

        {error && <div className="pt-4 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
        <FlatButton
          color="brand"
          onClick={handleSubmit}
          className="ml-2 px-8"
          disabled={!isValid || processing}
          loading={processing}
        >
          {t('common.action.send')}
        </FlatButton>
      </div>
    </Modal>
  );
};
